import { useEffect, useRef, useState, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Imperative dialog hook — same ergonomics as window.confirm/prompt but
// rendered as accessible, themed modals instead of OS chrome.
// ─────────────────────────────────────────────────────────────────────────────

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  initialValue?: string;
  required?: boolean;
  multiline?: boolean;
}

type ConfirmState = ConfirmOptions & {
  kind: 'confirm';
  resolve: (ok: boolean) => void;
};
type PromptState = PromptOptions & {
  kind: 'prompt';
  resolve: (value: string | null) => void;
};
type State = ConfirmState | PromptState | null;

export function useDialog() {
  const [state, setState] = useState<State>(null);
  const [text, setText] = useState('');
  const stateRef = useRef<State>(null);
  stateRef.current = state;

  useEffect(() => {
    if (state?.kind === 'prompt') setText(state.initialValue ?? '');
  }, [state]);

  const close = useCallback(() => setState(null), []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setState({ kind: 'confirm', ...opts, resolve });
      }),
    []
  );

  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setText(opts.initialValue ?? '');
        setState({ kind: 'prompt', ...opts, resolve });
      }),
    []
  );

  // ESC closes (treated as cancel)
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (stateRef.current?.kind === 'confirm') stateRef.current.resolve(false);
        else if (stateRef.current?.kind === 'prompt')
          stateRef.current.resolve(null);
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  const dialog = state ? (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-base font-semibold text-slate-900">
          {state.title}
        </h2>
        {state.message && (
          <p className="mb-4 whitespace-pre-line text-sm text-slate-600">
            {state.message}
          </p>
        )}
        {state.kind === 'prompt' &&
          (state.multiline ? (
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={state.placeholder}
              rows={3}
              className="mb-4 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          ) : (
            <input
              autoFocus
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={state.placeholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (state.required && !text.trim()) return;
                  state.resolve(text);
                  close();
                }
              }}
              className="mb-4 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          ))}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              if (state.kind === 'confirm') state.resolve(false);
              else state.resolve(null);
              close();
            }}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            {state.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            disabled={
              state.kind === 'prompt' && state.required && !text.trim()
            }
            onClick={() => {
              if (state.kind === 'confirm') state.resolve(true);
              else state.resolve(text);
              close();
            }}
            className={`rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
              state.destructive
                ? 'bg-rose-600 hover:bg-rose-700'
                : 'bg-brand-600 hover:bg-brand-700'
            }`}
          >
            {state.confirmLabel ?? (state.kind === 'prompt' ? 'OK' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, prompt, dialog };
}
